import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FHService, Product, ModalService } from 'npx-family-happy-common';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { ProductPricingConfig, ProductServiceDashboard } from '../../product-services/product-services';
import { CommonModule } from '@angular/common';
import { FHServiceSelectComponent } from 'src/app/shared/components/custom-select/fh-service-select/fh-service-select.component';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { BusinessLine } from 'src/app/shared/models/businessLine-model';

const DISCOUNT_ELIGIBLE_BUSINESS_LINES: string[] = [
  BusinessLine.COACH,
  BusinessLine.PSYCHOLOGIST,
  BusinessLine.NUTRITIONIST,
];


@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  standalone:true,
  imports:[CommonModule, ReactiveFormsModule, FHServiceSelectComponent, AlertComponent, NgbTypeaheadModule]
})
export class ProductDetailComponent implements OnInit, OnDestroy {

  form = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    name: new UntypedFormControl(null, [Validators.required]),
    businessLine: new UntypedFormControl(null, [Validators.required]),
    customerType: new UntypedFormControl(null, [Validators.required]),
    serviceName: new UntypedFormControl(null, [Validators.required]),
    serviceID: new UntypedFormControl(null, [Validators.required]),
    serviceScript: new UntypedFormControl(''),
    stripeID: new UntypedFormControl(''),
    type: new UntypedFormControl(null, [Validators.required]),
    service: new UntypedFormControl(null, [Validators.required]),
    disabled: new UntypedFormControl(null),
    b2bCompany: new UntypedFormControl(null),
  })

  discountForm = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    packageFlatCost: new UntypedFormControl(null, [Validators.required, Validators.min(1)]),
    discountPercentage: new UntypedFormControl(null, [Validators.required, Validators.min(0), Validators.max(100)]),
    discountedSessions: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
  })

  selectedProduct: Product | undefined;
  poolGroupId: string | null = null;
  isNew: boolean = false
  showDeleteConfirm:boolean = false;
  showDeleteDiscountConfirm: boolean = false;
  formInvalid = false
  discountFormInvalid = false
  private unsubscribe = new Subject<void>();

  constructor(private activatedRoute: ActivatedRoute, private router:Router, private productService: ProductServiceDashboard, private modalService:ModalService) { }

  get showDiscountSection(): boolean {
    return DISCOUNT_ELIGIBLE_BUSINESS_LINES.includes(this.form.get('businessLine')?.value);
  }

  get crossBusinessLineLabel(): string {
    if (!this.poolGroupId) {
      return 'Valido solo per questa businessline';
    }
    const labels: string[] = [];
    if (this.poolGroupId.includes('psy')) labels.push('Psicologi');
    if (this.poolGroupId.includes('nutr')) labels.push('Nutrizionisti');
    if (this.poolGroupId.includes('coach')) labels.push('Coach');
    return labels.length ? labels.join(' - ') : this.poolGroupId;
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap.pipe(takeUntil(this.unsubscribe)).subscribe((param) => {
      const id = param.get('id') ?? -1;
      if (id === 'create') {
        this.isNew = true;
      } else {
        if (id === -1) {
          this.goBackToProducts();
        } else {
          this.productService.getProductByID(+id).subscribe((product: Product) => {
            this.selectedProduct = product;
            this.initForm(product);
            this.loadDiscount(product.id);
          })
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  saveProduct(){
    if(this.form.valid){
      const formValue = this.form.value;
      const product: Product = {
        id: formValue.id,
        stripeID: formValue.stripeID,
        name: formValue.name,
        service: {
          id: formValue.serviceID,
          businessLine: formValue.businessLine,
          customerType: formValue.customerType,
          name: formValue.serviceName,
          script: formValue.serviceScript ?? '',
          disabled: formValue.disabled ?? false,
          b2bCompany: formValue.b2bCompany ? formValue.b2bCompany : null
        },
        type: formValue.type,
      }
      const isCreate = this.isNew;
      this.productService.saveProduct(product).subscribe({
        next: () => this.modalService.showSuccessMessage(isCreate ? 'Prodotto creato con successo' : 'Prodotto aggiornato'),
        error: (error) => this.modalService.showErrorMessage(
          this.buildErrorMessage(error, 'Impossibile salvare il prodotto.')
        ),
      });
    } else {
      this.formInvalid = true
      this.form.markAllAsTouched()
      this.modalService.showErrorMessage('Controlla i campi obbligatori: il prodotto non è stato salvato.')
    }
  }

  deleteProduct(){
    this.showDeleteConfirm = true;
  }

  confirmDelete(answer: boolean){
    if (answer && this.selectedProduct && this.selectedProduct.id > -1) {
      this.productService.deleteProductById(this.selectedProduct.id).subscribe(() => {
        this.modalService.showSuccessMessage('Prodotto eliminato');
        this.goBackToProducts();
      });
    }
    this.showDeleteConfirm = false;
  }

  loadDiscount(productId: number){
    this.productService.getProductPricingConfigByProductId(productId).pipe(
      catchError(() => of(null))
    ).subscribe((config) => {
      if (config) {
        this.discountForm.patchValue(config);
        this.poolGroupId = config.poolGroupId ?? null;
      }
    });
  }

  saveDiscount(){
    if (this.discountForm.valid && this.selectedProduct) {
      const formValue = this.discountForm.value;
      const config: ProductPricingConfig = {
        id: formValue.id,
        productId: this.selectedProduct.id,
        packageFlatCost: formValue.packageFlatCost,
        discountPercentage: formValue.discountPercentage,
        discountedSessions: formValue.discountedSessions,
      }
      const request = config.id
        ? this.productService.updateProductPricingConfig(config)
        : this.productService.saveProductPricingConfig(config);
      request.subscribe({
        next: (saved) => {
          this.discountForm.patchValue(saved);
          this.poolGroupId = saved.poolGroupId ?? null;
          this.modalService.showSuccessMessage('Sconto salvato');
        },
        error: (error) => this.modalService.showErrorMessage(
          this.buildErrorMessage(error, 'Impossibile salvare lo sconto.')
        ),
      });
    } else {
      this.discountFormInvalid = true;
    }
  }

  deleteDiscount(){
    this.showDeleteDiscountConfirm = true;
  }

  confirmDeleteDiscount(answer: boolean){
    const id = this.discountForm.get('id')?.value;
    if (answer && id) {
      this.productService.deleteProductPricingConfig(id).subscribe(() => {
        this.discountForm.reset({ id: null, packageFlatCost: null, discountPercentage: null, discountedSessions: null });
        this.poolGroupId = null;
        this.modalService.showSuccessMessage('Sconto eliminato');
      });
    }
    this.showDeleteDiscountConfirm = false;
  }

  updateFHService(service:FHService | undefined){
    if(service === undefined) {
      this.form.controls.businessLine.reset();
      this.form.controls.customerType.reset();
      this.form.controls.serviceName.reset();
      this.form.controls.serviceID.reset();
      this.form.controls.serviceScript.reset();
      this.form.controls.b2bCompany.reset();
      this.form.controls.service.reset();
    }
    this.form.controls.businessLine.setValue(service?.businessLine)
    this.form.controls.customerType.setValue(service?.customerType)
    this.form.controls.serviceName.setValue(service?.name)
    this.form.controls.serviceID.setValue(service?.id)
    this.form.controls.serviceScript.setValue(service?.script)
    this.form.controls.b2bCompany.setValue(service?.b2bCompany ?? null)
    this.form.controls.service.setValue(service)

  }

  validatorErrorMessage(fieldName:string, form: UntypedFormGroup = this.form){
    const field = form.get(fieldName);
    let message = ''
    if(field?.errors){
      if(field?.errors['required']){
        message = `Questo campo è obbligatorio`
      } else if(field?.errors['min']){
        message = `Il valore minimo è ${field.errors['min'].min}`
      } else if(field?.errors['max']){
        message = `Il valore massimo è ${field.errors['max'].max}`
      }
    }
    return message
  }


  goBackToProducts(){
    this.router.navigate(['/products/'])
  }

  private buildErrorMessage(error: unknown, fallback: string): string {
    const backendMessage = (error as any)?.error?.message ?? (error as any)?.message;
    return backendMessage ? `${fallback} (${backendMessage})` : fallback;
  }
  initForm(product:Product){
    this.form.patchValue({
      ...product,
      name: product.name,
      type: product.type,
      businessLine: product.service.businessLine,
      customerType: product.service.customerType,
      serviceName: product.service.name,
      serviceID: product.service.id,
      serviceScript: product.service.script,
      b2bCompany: product.service.b2bCompany ?? null,
      disabled:product.service.disabled
    })
  }
}
